import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Search, MoreVertical, Paperclip, Smile, Send, Check, CheckCheck, 
  Shield, ArrowLeft, Trash2, UserX, UserCheck, User, Info, MessageSquare, 
  X, ExternalLink, Heart, ThumbsUp, Frown, Ghost, Flame, Copy, Mail, AlertTriangle
} from 'lucide-react';
import { useSocket } from '../../../context/SocketContext';
import EmojiPicker from 'emoji-picker-react';
import { format, isToday, isYesterday } from 'date-fns';

const REACTION_EMOJIS = [
  { emoji: '👍', icon: <ThumbsUp size={14} /> },
  { emoji: '❤️', icon: <Heart size={14} /> },
  { emoji: '😂', icon: <Smile size={14} /> },
  { emoji: '😮', icon: <Ghost size={14} /> },
  { emoji: '😢', icon: <Frown size={14} /> },
  { emoji: '🔥', icon: <Flame size={14} /> },
];


export default function ChatSection({ preselectedUser = null }) {
  const { socket, onlineUsers } = useSocket();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessageText, setNewMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastMessages, setLastMessages] = useState({});
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const [showProfileSidebar, setShowProfileSidebar] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [reactionSource, setReactionSource] = useState(null); // { messageId, x, y }
  
  const messagesEndRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('placifyUser')) || {};
  const token = localStorage.getItem('placifyToken');

  const normalizeUserId = (value) => String(value || '').trim();

  const onlineUserSet = useMemo(() => {
    return new Set((onlineUsers || []).map((id) => normalizeUserId(id)).filter(Boolean));
  }, [onlineUsers]);

  const isUserOnline = (user) => {
    const userId = normalizeUserId(user?._id || user?.id || user?.userId);
    if (!userId) return Boolean(user?.isOnline);
    return onlineUserSet.has(userId) || Boolean(user?.isOnline);
  };

  // Fetch all users on mount
  const fetchUsers = React.useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
        
        const lastMsgs = {};
        const unreads = {};
        data.forEach(user => {
          if (user.lastMessage) lastMsgs[user._id] = user.lastMessage;
          if (user.unreadCount) unreads[user._id] = user.unreadCount;
        });
        setLastMessages(lastMsgs);
        setUnreadCounts(unreads);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [token, fetchUsers]);

  useEffect(() => {
    if (!preselectedUser) return;

    const matched = users.find((u) => u._id === preselectedUser._id);
    setSelectedUser(matched || preselectedUser);
  }, [preselectedUser, users]);

  // Fetch messages when a user is selected
  useEffect(() => {
    if (!selectedUser) return;
    
    const fetchMessages = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/messages/${selectedUser._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMessages(data);
          
          await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/messages/seen/${selectedUser._id}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setUnreadCounts(prev => ({ ...prev, [selectedUser._id]: 0 }));
        }
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      }
    };
    
    fetchMessages();
  }, [selectedUser, token]);


  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (msg) => {
      const isFromSelected = selectedUser && msg.senderId === selectedUser._id;
      const isForMe = msg.receiverId === currentUser.id;

      if (isFromSelected) {
        setMessages(prev => [...prev, msg]);
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/messages/seen/${msg.senderId}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (isForMe) {
        setUnreadCounts(prev => ({
          ...prev,
          [msg.senderId]: (prev[msg.senderId] || 0) + 1
        }));
      }

      const otherUserId = msg.senderId === currentUser.id ? msg.receiverId : msg.senderId;
      setLastMessages(prev => ({
        ...prev,
        [otherUserId]: {
          text: msg.text,
          createdAt: msg.createdAt,
          senderId: msg.senderId
        }
      }));
    };

    const handleMessagesSeen = ({ seenBy }) => {
      if (selectedUser && seenBy === selectedUser._id) {
        setMessages(prev => prev.map(m => m.receiverId === seenBy ? { ...m, seen: true } : m));
      }
    };

    const handleReaction = ({ messageId, reactions }) => {
       setMessages(prev => prev.map(m => m._id === messageId ? { ...m, reactions } : m));
    };

    socket.on('newMessage', handleNewMessage);
    socket.on('messagesSeen', handleMessagesSeen);
    socket.on('messageReaction', handleReaction);

    return () => {
      socket.off('newMessage', handleNewMessage);
      socket.off('messagesSeen', handleMessagesSeen);
      socket.off('messageReaction', handleReaction);
    };
  }, [socket, selectedUser, currentUser.id, token]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessageText.trim() || !selectedUser) return;

    const tempText = newMessageText;
    setNewMessageText('');
    setShowEmojiPicker(false);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/messages/send/${selectedUser._id}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ text: tempText })
      });
      
      if (res.ok) {
        const savedMsg = await res.json();
        setMessages(prev => [...prev, savedMsg]);
        setLastMessages(prev => ({
          ...prev,
          [selectedUser._id]: {
            text: savedMsg.text,
            createdAt: savedMsg.createdAt,
            senderId: savedMsg.senderId
          }
        }));
      } else {
        const errData = await res.json();
        alert(errData.message || 'Failed to send');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleToggleReaction = async (messageId, emoji) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/messages/react/${messageId}`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ emoji })
      });
      if (res.ok) {
        const updatedReactions = await res.json();
        setMessages(prev => prev.map(m => m._id === messageId ? { ...m, reactions: updatedReactions } : m));
      }
    } catch (err) {
      console.error('Reaction failed:', err);
    }
    setReactionSource(null);
  };

  const handleClearChat = async () => {
    if (!selectedUser) return;
    if (!window.confirm('Are you sure you want to clear this chat? This only clears it for you.')) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/messages/clear/${selectedUser._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMessages([]);
        setLastMessages(prev => ({ ...prev, [selectedUser._id]: null }));
        setShowOptionsDropdown(false);
      }
    } catch (err) {
      console.error('Clear chat failed:', err);
    }
  };

  const handleBlockUserById = async (targetUserId, currentlyBlocked = false) => {
    if (!targetUserId) return;
    const isBlocked = Boolean(currentlyBlocked);
    const action = isBlocked ? 'unblock' : 'block';
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/${action}/${targetUserId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedUser(prev => prev && normalizeUserId(prev._id) === normalizeUserId(targetUserId)
          ? { ...prev, isBlocked: !isBlocked }
          : prev
        );
        setViewingProfile(prev => prev && normalizeUserId(prev._id || prev.id || prev.userId) === normalizeUserId(targetUserId)
          ? { ...prev, isBlocked: !isBlocked }
          : prev
        );
        setUsers(prev => prev.map(user => normalizeUserId(user._id) === normalizeUserId(targetUserId)
          ? { ...user, isBlocked: !isBlocked }
          : user
        ));
        fetchUsers();
        setShowOptionsDropdown(false);
      }
    } catch (err) {
      console.error(`${action} failed:`, err);
    }
  };

  const handleBlockUser = async () => {
    if (!selectedUser) return;
    await handleBlockUserById(selectedUser._id, selectedUser.isBlocked);
  };

  const handleCopyToClipboard = async (value, label) => {
    if (!value) {
      window.alert(`${label} is not available for this profile.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(String(value));
      window.alert(`${label} copied successfully.`);
    } catch (err) {
      console.error('Copy failed:', err);
      window.alert(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  const handleReportUser = (user) => {
    const userId = user?._id || user?.id || user?.userId;
    const userName = user?.name || 'Unknown user';
    if (!userId) {
      window.alert('Unable to report this user right now.');
      return;
    }

    const reason = window.prompt('Report reason (required):');
    if (!reason || !reason.trim()) return;

    const subject = encodeURIComponent(`Placify report for ${userName}`);
    const body = encodeURIComponent(
      `Please review this user report.\n\nReported user: ${userName}\nUser ID: ${userId}\nReason: ${reason.trim()}\nReported at: ${new Date().toISOString()}`
    );

    window.location.href = `mailto:placify.support@gmail.com?subject=${subject}&body=${body}`;
    window.alert('Report draft opened in your email app.');
  };

  const handleViewProfile = (user) => {
    setViewingProfile(user);
    setShowProfileSidebar(true);
    setShowOptionsDropdown(false);
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return format(date, 'HH:mm');
  };

  const getDateLabel = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'TODAY';
    if (isYesterday(date)) return 'YESTERDAY';
    return format(date, 'MMMM d, yyyy').toUpperCase();
  };

  return (
    <div className="flex flex-col lg:flex-row w-full h-[100vh] bg-[#0A0A0A] overflow-hidden relative text-[#F5F0EB] font-['DM_Sans']">
      
      {/* 2. USER LIST (LEFT PANEL) - SLEEK DARK */}
      <div className={`flex flex-col h-full w-full lg:w-[380px] border-r border-[#2A2520] bg-[#111111] ${selectedUser ? 'hidden lg:flex' : 'flex'}`}>
        
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-[#2A2520] flex items-center justify-between bg-[#1C1C1C]/30 backdrop-blur-xl">
          <div className="flex items-center gap-3 sm:gap-4">
             <div 
              onClick={() => handleViewProfile({...currentUser, name: currentUser.name || 'My Profile'})}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-[14px] sm:rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#E8A430] flex items-center justify-center cursor-pointer hover:rotate-3 transition-all duration-300 font-bold border border-[#2A2520] shadow-lg overflow-hidden group shrink-0"
             >
               {currentUser.avatar ? 
                 <img src={currentUser.avatar} alt="Me" className="w-full h-full object-cover group-hover:scale-110 transition-transform" /> : 
                 <span className="text-white text-base sm:text-lg">{currentUser.name?.charAt(0).toUpperCase()}</span>
               }
             </div>
             <div>
               <h2 className="text-[10px] sm:text-[11px] font-['JetBrains_Mono']l font-extrabold font-['Syne'] text-white tracking-tight">Messages</h2>
               <p className="text-[9px] sm:text-[10px] text-[#FF6B35] font-bold uppercase tracking-widest">Placify Network</p>
             </div>
          </div>
          <button className="text-[#A89E94] hover:text-white p-2 rounded-xl hover:bg-white/5 transition-all">
            <MoreVertical className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Search bar */}
        <div className="p-4">
          <div className="relative flex items-center bg-[#1C1C1C] border border-[#2A2520] rounded-2xl px-4 py-3 focus-within:ring-2 focus-within:ring-[#FF6B35]/50 transition-all shadow-inner">
            <Search size={18} className="text-[#A89E94]" />
            <input 
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-sm font-['DM_Sans'] text-white placeholder:text-[#5C5550] ml-3 w-full font-medium"
            />
          </div>
        </div>

        {/* Users Loop */}
        <div className="flex-1 overflow-y-auto custom-scrollbar no-scrollbar py-2">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <div 
                key={user._id}
                onClick={() => setSelectedUser(user)}
                className={`mx-3 mb-1 flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-200 group ${
                  selectedUser?._id === user._id 
                    ? 'bg-[#FF6B35] shadow-[0_10px_20px_rgba(79,70,229,0.3)] ring-1 ring-white/20' 
                    : 'hover:bg-white/5'
                }`}
              >
                {/* Avatar */}
                <div className="relative shrink-0">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-[14px] sm:rounded-2xl flex items-center justify-center text-white font-bold text-[10px] font-['JetBrains_Mono']l border-2 transition-all group-hover:scale-105 ${
                    selectedUser?._id === user._id ? 'border-[#FF6B35] shadow-lg' : 'border-[#2A2520]'
                  } bg-gradient-to-br ${
                    user.role === 'Placed' ? 'from-[#E8A430] to-[#E8A430]' : 
                    user.role === 'Professional' ? 'from-[#FF6B35] to-[#FF6B35]' : 'from-[#FF3D00] to-[#E8A430]'
                  }`}>
                    {user.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full rounded-[14px] sm:rounded-2xl object-cover" /> : user.name.charAt(0).toUpperCase()}
                  </div>
                  {isUserOnline(user) && (
                    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-emerald-500 border-2 border-[#111111] rounded-full shadow-lg" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline mb-1">
                    <h3 className={`font-bold truncate ${selectedUser?._id === user._id ? 'text-white' : 'text-[#F5F0EB]'}`}>
                      {user.name}
                    </h3>
                    <span className={`text-[10px] font-medium whitespace-nowrap ${selectedUser?._id === user._id ? 'text-[#E8A430]' : 'text-[#5C5550]'}`}>
                      {lastMessages[user._id] ? formatMessageTime(lastMessages[user._id].createdAt) : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter shadow-sm ${
                       selectedUser?._id === user._id 
                        ? 'bg-white/20 text-white' 
                        : user.role === 'Placed' ? 'bg-emerald-500/10 text-emerald-400' : 
                          user.role === 'Professional' ? 'bg-blue-500/10 text-blue-400' : 'bg-orange-500/10 text-orange-400'
                    }`}>
                      {user.role}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-[13px]">
                    <p className={`truncate flex-1 pr-4 ${
                      selectedUser?._id === user._id 
                        ? 'text-[#E8A430]' 
                        : unreadCounts[user._id] > 0 ? 'text-white font-bold' : 'text-[#A89E94]'
                    }`}>
                      {lastMessages[user._id] ? lastMessages[user._id].text : 'Start a professional talk'}
                    </p>
                    {unreadCounts[user._id] > 0 && selectedUser?._id !== user._id && (
                      <span className="bg-white text-[#FF6B35] font-black text-[10px] min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 shadow-lg ring-2 ring-[#FF6B35]/20">
                        {unreadCounts[user._id]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-3xl bg-[#1C1C1C] flex items-center justify-center mx-auto mb-4 border border-[#2A2520]">
                <Search size={24} className="text-[#5C5550]" />
              </div>
              <p className="text-[#5C5550] text-sm font-['DM_Sans'] font-medium">No conversations found</p>
            </div>
          )}
        </div>
      </div>

      {/* 3. CHAT WINDOW (RIGHT PANEL) - PROFESSIONAL DARK */}
      <div className={`flex-1 h-full flex flex-col bg-[#0A0A0A] relative ${!selectedUser ? 'hidden lg:flex' : 'flex'}`}>
        {selectedUser ? (
          <>
            {/* Header */}
            <div className="px-4 py-3 sm:px-8 sm:py-5 border-b border-[#2A2520] bg-[#111111]/80 backdrop-blur-2xl flex items-center justify-between shrink-0 z-10 shadow-[0_1px_3px_rgba(0,0,0,0.3)]">
              <div className="flex items-center gap-3 sm:gap-5 cursor-pointer group" onClick={() => handleViewProfile(selectedUser)}>
                <button 
                  onClick={(e) => { e.stopPropagation(); setSelectedUser(null); }} 
                  className="lg:hidden p-1.5 sm:p-2 -ml-2 text-[#A89E94] hover:text-white hover:bg-white/5 rounded-xl transition-all"
                >
                  <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
                <div className="relative shrink-0">
                   <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-[14px] sm:rounded-2xl flex items-center justify-center text-white font-bold text-[10px] font-['JetBrains_Mono']l bg-gradient-to-br shadow-xl group-hover:scale-105 transition-transform ${
                    selectedUser.role === 'Placed' ? 'from-[#E8A430] to-[#E8A430]' : 
                    selectedUser.role === 'Professional' ? 'from-[#FF6B35] to-[#FF3D00]' : 'from-[#FF3D00] to-[#E8A430]'
                  }`}>
                    {selectedUser.avatar ? <img src={selectedUser.avatar} alt="User" className="w-full h-full rounded-[14px] sm:rounded-2xl object-cover" /> : selectedUser.name.charAt(0).toUpperCase()}
                  </div>
                  {onlineUsers.includes(selectedUser._id) && (
                    <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-emerald-500 border-2 border-[#111111] rounded-full shadow-lg" />
                  )}
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-white tracking-tight leading-none mb-1.5">{selectedUser.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${isUserOnline(selectedUser) ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-600'}`}></span>
                    <p className={`text-[9px] sm:text-[11px] font-bold uppercase tracking-widest ${isUserOnline(selectedUser) ? 'text-emerald-400' : 'text-[#5C5550]'}`}>
                      {isUserOnline(selectedUser) ? 'ACTIVE NOW' : 'OFFLINE'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleViewProfile(selectedUser)}
                  className="text-[#A89E94] hover:text-[#FF6B35] p-2.5 rounded-xl hover:bg-[#FF6B35]/10 transition-all"
                >
                  <Info size={20} />
                </button>
                <div className="relative">
                  <button onClick={() => setShowOptionsDropdown(!showOptionsDropdown)} className="text-[#A89E94] hover:text-white p-2.5 rounded-xl hover:bg-white/5 transition-all">
                    <MoreVertical size={20} />
                  </button>
                  {showOptionsDropdown && (
                    <div className="absolute right-0 top-full mt-3 w-56 bg-[#1C1C1C] border border-[#2A2520] rounded-2xl shadow-2xl py-2 z-50 animate-in fade-in slide-in-from-top-4 duration-300 ring-1 ring-black/20">
                      <button onClick={() => handleViewProfile(selectedUser)} className="px-5 py-3 text-left text-sm font-['DM_Sans'] text-[#F5F0EB] hover:bg-[#FF6B35] hover:text-white flex items-center gap-4 transition-all mx-1 rounded-xl w-[calc(100%-8px)]">
                        <User size={18} /> View Dynamic Profile
                      </button>
                      <div className="h-px bg-white/5 my-1 mx-4"></div>
                      <button onClick={handleClearChat} className="px-5 py-3 text-left text-sm font-['DM_Sans'] text-red-400 hover:bg-red-500/10 flex items-center gap-4 transition-all mx-1 rounded-xl w-[calc(100%-8px)]">
                        <Trash2 size={18} /> Purge Chat History
                      </button>
                      <button onClick={handleBlockUser} className="px-5 py-3 text-left text-sm font-['DM_Sans'] text-[#A89E94] hover:bg-white/5 flex items-center gap-4 transition-all mx-1 rounded-xl w-[calc(100%-8px)]">
                        {selectedUser.isBlocked ? (
                          <><UserCheck size={18} className="text-emerald-400" /> Restore Access</>
                        ) : (
                          <><UserX size={18} className="text-orange-400" /> Restrict Contact</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Messages Area - PROFESSIONAL DARK OVERHAUL */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 custom-scrollbar relative bg-[#0A0A0A] professional-chat-bg">
              {/* Subtle background overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.02] to-purple-500/[0.02] pointer-events-none" />
              
              <div className="flex justify-center mb-6 sm:mb-10 relative z-10">
                <div className="bg-[#FF6B35]/10 backdrop-blur-md border border-[#FF6B35]/20 px-4 py-2 sm:px-5 sm:py-2.5 rounded-2xl text-[9px] sm:text-[11px] text-[#FF6B35] shadow-xl flex items-center gap-2 sm:gap-3 max-w-[90%] sm:max-w-sm text-center font-bold tracking-wide">
                  <Shield className="w-3.5 h-3.5 sm:w-[14px] sm:h-[14px] text-[#FF6B35] shrink-0" />
                  THIS CONVERSATION IS PROTECTED BY ENTERPRISE ENCRYPTION
                </div>
              </div>

              {messages.map((msg, idx) => {
                const isMine = msg.senderId === currentUser.id;
                const showDate = idx === 0 || getDateLabel(messages[idx-1].createdAt) !== getDateLabel(msg.createdAt);
                const reactions = msg.reactions || [];
                
                return (
                  <React.Fragment key={msg._id || idx}>
                    {showDate && (
                      <div className="flex justify-center my-6 sm:my-8 relative z-10">
                        <span className="px-4 py-1 sm:px-5 sm:py-1.5 bg-[#1C1C1C]/50 backdrop-blur-lg text-[9px] sm:text-[10px] font-black text-[#A89E94] rounded-full uppercase tracking-[0.2em] border border-[#2A2520] shadow-lg">
                          {getDateLabel(msg.createdAt)}
                        </span>
                      </div>
                    )}
                    <div className={`flex group ${isMine ? 'justify-end' : 'justify-start'} relative mb-2 z-10`}>
                      <div 
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setReactionSource({ messageId: msg._id, x: e.clientX, y: e.clientY });
                        }}
                        className={`relative max-w-[85%] sm:max-w-[70%] px-4 py-2.5 sm:px-5 sm:py-3.5 rounded-2xl sm:rounded-3xl shadow-xl transition-all duration-300 hover:scale-[1.01] ${
                          isMine 
                            ? 'bg-gradient-to-br from-[#FF6B35] to-[#FF3D00] text-white rounded-tr-none shadow-[#FF6B35]/20' 
                            : 'bg-[#1C1C1C] text-[#F5F0EB] rounded-tl-none border border-[#2A2520] shadow-black/40'
                        }`}
                      >
                        <div className="text-[14px] sm:text-[15px] pr-2 sm:pr-4 pb-2 leading-relaxed font-medium">
                          {msg.text}
                        </div>

                        {/* Reaction Bar (Hover only) */}
                        <div className={`absolute bottom-full mb-2 ${isMine ? 'right-0' : 'left-0'} opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 flex items-center gap-1.5 bg-[#1C1C1C] p-1.5 rounded-2xl shadow-2xl border border-[#2A2520] z-50`}>
                           {REACTION_EMOJIS.slice(0, 4).map(re => (
                             <button key={re.emoji} onClick={() => handleToggleReaction(msg._id, re.emoji)} className="hover:scale-125 transition-transform p-1.5 grayscale hover:grayscale-0">
                                {re.emoji}
                             </button>
                           ))}
                           <button onClick={(e) => setReactionSource({ messageId: msg._id, x: e.clientX, y: e.clientY })} className="p-1.5 text-[#A89E94] hover:text-white"><Smile size={16} /></button>
                        </div>

                        {/* Current Reactions Display */}
                        {reactions.length > 0 && (
                          <div className={`absolute -bottom-4 ${isMine ? 'right-2' : 'left-2'} flex items-center gap-1 bg-[#111111]/90 backdrop-blur-md border border-[#2A2520] rounded-full px-2.5 py-1 shadow-2xl scale-95 hover:scale-110 transition-transform cursor-default z-20`}>
                            {Array.from(new Set(reactions.map(r => r.emoji))).slice(0, 3).map(emoji => (
                              <span key={emoji} className="text-[13px]">{emoji}</span>
                            ))}
                            <span className="text-[10px] text-[#A89E94] ml-1 font-black">{reactions.length}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2 mt-2 justify-end opacity-70">
                          <span className="text-[10px] font-bold">
                            {formatMessageTime(msg.createdAt)}
                          </span>
                          {isMine && (
                            msg.seen ? (
                              <CheckCheck size={14} className="text-cyan-300" />
                            ) : (
                              <Check size={14} className="text-white/40" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Bar - PROFESSIONAL DARK */}
            <div className="p-3 sm:p-6 bg-[#111111] border-t border-[#2A2520] shrink-0 relative z-20 shadow-[0_-10px_30px_rgba(0,0,0,0.3)]">
              {showEmojiPicker && (
                <div className="absolute bottom-full left-3 sm:left-6 mb-2 sm:mb-4 z-50 shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden ring-1 ring-white/10 max-h-[300px] overflow-y-auto">
                  <EmojiPicker theme="dark" onEmojiClick={(emojiData) => setNewMessageText(prev => prev + emojiData.emoji)} />
                </div>
              )}
              
              {selectedUser.isBlocked ? (
                <div className="flex flex-col items-center justify-center p-4 sm:p-6 bg-red-500/5 rounded-[20px] sm:rounded-3xl border border-red-500/10 backdrop-blur-sm">
                  <p className="text-xs sm:text-sm font-['DM_Sans'] text-red-400 mb-3 sm:mb-4 font-bold tracking-tight text-center">Access restricted for this contact</p>
                  <button onClick={handleBlockUser} className="px-6 py-2 sm:px-10 sm:py-3 bg-[#FF6B35] text-white rounded-xl sm:rounded-2xl text-[10px] font-['JetBrains_Mono']s font-black shadow-lg shadow-[#FF6B35]/40 hover:scale-105 active:scale-95 transition-all uppercase tracking-widest text-center">
                    Restore Communication
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSendMessage} className="flex items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-1 bg-[#1C1C1C] p-1 rounded-[14px] sm:rounded-2xl border border-[#2A2520]">
                    <button 
                      type="button" 
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className={`p-2 sm:p-3 transition-all rounded-[10px] sm:rounded-xl ${showEmojiPicker ? 'text-[#FF6B35] bg-[#FF6B35]/10' : 'text-[#A89E94] hover:text-white hover:bg-white/5'}`}
                    >
                      <Smile className="w-5 h-5 sm:w-[22px] sm:h-[22px]" />
                    </button>
                    <button 
                      type="button" 
                      className="p-2 sm:p-3 text-[#A89E94] hover:text-white hover:bg-white/5 rounded-[10px] sm:rounded-xl transition-all hidden sm:block"
                    >
                      <Paperclip className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                  <div className="flex-1 relative">
                    <input 
                      type="text"
                      placeholder="Type a message..."
                      value={newMessageText}
                      onChange={(e) => setNewMessageText(e.target.value)}
                      className="w-full bg-[#1C1C1C] border border-[#2A2520] outline-none py-3 px-4 sm:py-4 sm:px-6 rounded-[20px] sm:rounded-3xl text-[14px] sm:text-[15px] text-white placeholder:text-[#5C5550] shadow-inner focus:ring-2 focus:ring-[#FF6B35]/50 transition-all font-medium"
                    />
                  </div>
                  <button 
                    type="submit"
                    disabled={!newMessageText.trim()}
                    className={`p-3 sm:p-4 rounded-[16px] sm:rounded-2xl transition-all duration-300 shadow-xl shrink-0 border-none cursor-pointer ${newMessageText.trim() ? 'bg-[#FF6B35] text-white hover:scale-105 hover:shadow-[#FF6B35]/30 active:scale-95' : 'bg-[#1C1C1C] text-[#5C5550]'}`}
                  >
                    <Send className={`w-5 h-5 sm:w-[22px] sm:h-[22px] ${newMessageText.trim() ? 'rotate-[-10deg]' : ''}`} fill={newMessageText.trim() ? 'currentColor' : 'none'} />
                  </button>
                </form>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12 bg-[#0A0A0A] relative overflow-hidden">
             {/* Dynamic background effects */}
            <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] bg-[#FF6B35]/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
            
            <div className="w-32 h-32 bg-[#FF6B35]/10 rounded-[40px] border border-[#FF6B35]/20 flex items-center justify-center mb-10 relative group">
               <div className="absolute inset-[-15px] bg-[#FF6B35]/10 rounded-[50px] animate-pulse blur-xl opacity-30" />
               <MessageSquare size={56} className="text-[#FF6B35] group-hover:scale-110 transition-transform duration-500" />
            </div>
            <h2 className="text-4xl font-black font-['Syne'] text-white mb-6 tracking-tight">Placify <span className="text-[#FF6B35]">Elite</span> Chat</h2>
            <p className="text-[#A89E94] max-w-md leading-relaxed text-base font-medium">
              Seamless. Secure. Professional. <br />
              Connect with India's top placed students and industry mentors in a clutter-free environment.
            </p>
            <div className="mt-16 grid grid-cols-2 gap-4 w-full max-w-sm">
                <div className="p-4 bg-[#111111] border border-[#2A2520] rounded-2xl text-left">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                    <Shield size={16} className="text-emerald-400" />
                  </div>
                  <p className="text-[11px] font-bold text-white uppercase mb-1">Encrypted</p>
                  <p className="text-[10px] text-[#5C5550]">End-to-end security protocol</p>
                </div>
                <div className="p-4 bg-[#111111] border border-[#2A2520] rounded-2xl text-left">
                  <div className="w-8 h-8 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center mb-3">
                    <Flame size={16} className="text-[#FF6B35]" />
                  </div>
                  <p className="text-[11px] font-bold text-white uppercase mb-1">Direct</p>
                  <p className="text-[10px] text-[#5C5550]">Real-time low latency delivery</p>
                </div>
            </div>
          </div>
        )}
      </div>

      {/* 4. PROFILE SIDEBAR - PROFESSIONAL STYLE */}
      {showProfileSidebar && viewingProfile && (
        <div className="absolute inset-0 bg-[#0A0A0A]/80 backdrop-blur-md z-[100] flex justify-end animate-in fade-in duration-500">
          <div className="w-full sm:w-[420px] bg-[#111111] h-full shadow-[0_0_100px_rgba(0,0,0,0.8)] border-l border-[#2A2520] flex flex-col animate-in slide-in-from-right duration-500">
            {/* Header */}
            <div className="p-4 sm:p-8 flex items-center justify-between bg-[#1C1C1C]/30">
              <h2 className="text-[10px] font-['JetBrains_Mono']l font-bold text-white tracking-tight">Profile Details</h2>
              <button 
                onClick={() => setShowProfileSidebar(false)}
                className="p-2 sm:p-3 bg-white/5 hover:bg-white/10 rounded-[12px] sm:rounded-2xl text-[#A89E94] hover:text-white transition-all shadow-lg border-none"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 sm:p-10 flex flex-col items-center">
               <div className="relative mb-6 sm:mb-8 group">
                 <div className="absolute inset-0 bg-indigo-500/20 rounded-[40px] sm:rounded-[50px] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                 <div className={`w-32 h-32 sm:w-48 sm:h-48 rounded-[40px] sm:rounded-[60px] flex items-center justify-center text-white font-black text-4xl sm:text-6xl shadow-2xl relative border-[4px] sm:border-[6px] border-[#111111] bg-gradient-to-br transform group-hover:rotate-2 transition-all duration-500 overflow-hidden ${
                    viewingProfile.role?.includes('Placed') ? 'from-[#E8A430] to-[#E8A430]' : 
                    viewingProfile.role?.includes('Professional') ? 'from-[#FF6B35] to-[#FF3D00]' : 'from-[#FF3D00] to-[#E8A430]'
                 }`}>
                   {viewingProfile.avatar ? <img src={viewingProfile.avatar} alt="Profile" className="w-full h-full object-cover" /> : viewingProfile.name?.charAt(0).toUpperCase()}
                 </div>
                 {isUserOnline(viewingProfile) && (
                   <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 w-4 h-4 sm:w-6 sm:h-6 bg-emerald-500 border-2 sm:border-4 border-[#111111] rounded-full shadow-xl" />
                 )}
               </div>
               
               <h3 className="text-2xl sm:text-[32px] leading-tight font-extrabold font-['DM_Sans'] text-white mb-2 text-center tracking-tight break-words max-w-full">{viewingProfile.name}</h3>
               <p className="text-xs font-bold uppercase tracking-[0.2em] mb-5 flex items-center gap-2 text-center">
                 <span className={`w-2.5 h-2.5 rounded-full ${isUserOnline(viewingProfile) ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.45)]' : 'bg-slate-600'}`}></span>
                 <span className={isUserOnline(viewingProfile) ? 'text-emerald-400' : 'text-[#A89E94]'}>{isUserOnline(viewingProfile) ? 'ONLINE' : 'OFFLINE'}</span>
               </p>
               <div className="px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full mb-6">
                 <p className="text-[#FF6B35] text-[10px] font-['JetBrains_Mono']s font-black uppercase tracking-widest">{viewingProfile.role || 'PLACIFY MEMBER'}</p>
               </div>

               <div className="w-full space-y-4 mb-10">
                  <div className="w-full bg-[#1C1C1C]/50 backdrop-blur-sm rounded-[32px] p-6 border border-[#2A2520] shadow-inner">
                    <p className="text-[10px] text-[#FF6B35] font-black uppercase tracking-[0.2em] mb-3">Professional Contact</p>
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-3 group">
                        <span className="text-base leading-tight font-['DM_Sans'] text-[#A89E94] font-medium">Email</span>
                        <span className="text-base leading-tight text-right break-all font-['DM_Sans'] text-[#F5F0EB] font-semibold group-hover:text-[#FF6B35] transition-colors">{viewingProfile.email || 'Restricted'}</span>
                      </div>
                      {viewingProfile.email && (
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => window.open(`mailto:${viewingProfile.email}`, '_blank', 'noopener,noreferrer')}
                            className="py-2.5 rounded-xl bg-[#111111] border border-[#2A2520] text-[#F5F0EB] text-xs font-bold uppercase tracking-wider hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] transition-all flex items-center justify-center gap-2"
                          >
                            <Mail size={14} /> Email
                          </button>
                          <button
                            onClick={() => handleCopyToClipboard(viewingProfile.email, 'Email')}
                            className="py-2.5 rounded-xl bg-[#111111] border border-[#2A2520] text-[#F5F0EB] text-xs font-bold uppercase tracking-wider hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] transition-all flex items-center justify-center gap-2"
                          >
                            <Copy size={14} /> Copy
                          </button>
                        </div>
                      )}
                      <div className="h-px bg-white/5"></div>
                      <div className="flex items-center justify-between gap-3 group">
                        <span className="text-base font-['DM_Sans'] text-[#A89E94] font-medium">Network ID</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-[#8D837A] font-bold uppercase">{viewingProfile._id?.slice(-8) || 'N/A'}</span>
                          {viewingProfile._id && (
                            <button
                              onClick={() => handleCopyToClipboard(viewingProfile._id, 'Network ID')}
                              className="p-1.5 rounded-lg bg-[#111111] border border-[#2A2520] text-[#A89E94] hover:text-[#FF6B35] hover:border-[#FF6B35]/40 transition-all"
                              aria-label="Copy Network ID"
                            >
                              <Copy size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {viewingProfile.linkedinUrl && (
                    <a 
                      href={viewingProfile.linkedinUrl} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="w-full bg-[#1C1C1C] hover:bg-[#FF6B35] hover:translate-y-[-2px] rounded-2xl p-5 flex items-center justify-between transition-all duration-300 group border border-[#2A2520] shadow-xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:bg-white/20">
                          <ExternalLink size={18} className="text-blue-400 group-hover:text-white" />
                        </div>
                        <span className="text-sm font-['DM_Sans'] font-bold text-[#F5F0EB] group-hover:text-white">LinkedIn Presence</span>
                      </div>
                      <ArrowLeft size={18} className="rotate-180 text-[#5C5550] group-hover:text-white" />
                    </a>
                  )}
               </div>

               <div className="w-full space-y-2 mt-auto">
                  {(viewingProfile._id && normalizeUserId(viewingProfile._id) !== normalizeUserId(currentUser.id)) ? (
                    <div className="w-full grid grid-cols-2 gap-4">
                      <button
                        onClick={() => handleBlockUserById(viewingProfile._id, viewingProfile.isBlocked)}
                        className="py-4 bg-[#111111] hover:bg-red-500/10 rounded-2xl text-red-400 text-[10px] font-['JetBrains_Mono']s font-black flex items-center justify-center gap-3 transition-all border border-[#2A2520] uppercase tracking-widest active:scale-95"
                      >
                        <UserX size={18} /> {viewingProfile.isBlocked ? 'Unblock' : 'Block'}
                      </button>
                      <button
                        onClick={() => handleReportUser(viewingProfile)}
                        className="py-4 bg-[#111111] hover:bg-orange-500/10 rounded-2xl text-orange-400 text-[10px] font-['JetBrains_Mono']s font-black flex items-center justify-center gap-3 transition-all border border-[#2A2520] uppercase tracking-widest active:scale-95"
                      >
                        <AlertTriangle size={18} /> Report
                      </button>
                    </div>
                  ) : (
                    <div className="w-full rounded-2xl border border-[#2A2520] bg-[#1C1C1C]/50 px-4 py-3 text-center text-xs font-semibold text-[#A89E94]">
                      This is your profile. Community moderation actions are hidden.
                    </div>
                  )}
               </div>
            </div>
          </div>
        </div>
      )}

      {/* REACTION POPUP - SLEEK STYLE */}
      {reactionSource && (
        <div 
          className="fixed inset-0 z-[200] bg-[#0A0A0A]/40 backdrop-blur-sm" 
          onClick={() => setReactionSource(null)}
        >
          <div 
            className="fixed bg-[#1C1C1C] border border-[#2A2520] rounded-[32px] p-3 shadow-2xl flex items-center gap-4 animate-in zoom-in-50 duration-300 ring-4 ring-black/20"
            style={{ 
              left: `${Math.min(reactionSource.x, window.innerWidth - 300)}px`, 
              top: `${Math.min(reactionSource.y - 80, window.innerHeight - 100)}px` 
            }}
          >
            {REACTION_EMOJIS.map(re => (
              <button 
                key={re.emoji} 
                onClick={(e) => { e.stopPropagation(); handleToggleReaction(reactionSource.messageId, re.emoji); }} 
                className="text-3xl hover:scale-150 active:scale-110 transition-all duration-200 p-1 drop-shadow-lg"
              >
                {re.emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 20px;
        }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
        }
        .professional-chat-bg {
          background-color: #0A0A0A;
          background-image: 
            radial-gradient(circle at 0% 0%, rgba(255, 107, 53, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 100% 100%, rgba(232, 164, 48, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(10, 10, 10, 1) 0%, rgba(10, 10, 10, 0.8) 100%);
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
      `}} />
    </div>
  );
}
